import { AsyncPipe, NgClass } from '@angular/common';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, effect, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map, mergeMap, Observable, of, take } from 'rxjs';

import { EnvironmentService, IdUtils, UaService } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';
import { EcmMaterialModule, InsecablePipe } from '@ecm/material';
import { OptionCode, PortfolioService } from '@ecm/portfolio';
import { ErrorPageService, ExtendedLink, Link, LinkService, RouterService } from '@ecm/router';
import { KenobiParams, Status, TrackableView } from '@ecm/tracker';

import { UniverseEnum } from '../../../business/api/dashboard/universeEnum';
import { BrandEnum } from '../../../business/api/dashboardV7/brandEnum';
import { DashboardV7 } from '../../../business/api/dashboardV7/dashboardV7';
import { ActionEnum } from '../../../business/api/dashboardV7/device/actions';
import { DeviceAction } from '../../../business/api/dashboardV7/device/deviceAction';
import { TypeEnum } from '../../../business/api/dashboardV7/push/typeEnum';
import { DashboardV7ViewModelMapper } from '../../../business/mapper/home/dashboard/dashboardV7.view-model-mapper';
import { DashboardV7ViewModel } from '../../../business/model/home/dashboard/dashboardV7.view-model';
import { SectionV7ViewModel } from '../../../business/model/home/sectionV7.view-model';
import { DeviceStatus } from '../../../business/model/homelan/deviceStatus.view-model';
import { HomeLanServiceStatusEnum, TesterDepannerDomainEnum } from '../../../business/model/homelan/homelan.view-model';
import { KenobiCodes } from '../../../business/model/tracking/kenobiCodes';
import { TmsCodes } from '../../../business/model/tracking/tmsCodes.enum';
import { ForbiddenComponent } from '../../../features/forbidden/container/forbidden/forbidden.component';
import { BreakpointService } from '../../../services/breakpoint.service';
import { DashboardService } from '../../../services/dashboard.service';
import { HomeLanService } from '../../../services/homelan.service';
import { TrackingService } from '../../../services/tracking.service';
import { AbstractParentComponent } from '../../shared/abstract-parent.component';
import { ItemComponent } from '../item/item.component';
import { MegapushComponent } from '../megapush/megapush.component';
import { PushAlertsComponent } from '../push-alerts/push-alerts.component';
import { PushChangeOfferComponent } from '../push-change-offer/push-change-offer.component';
import { PushCommercialComponent } from '../push-commercial/push-commercial.component';
import { PushHelpsComponent } from '../push-helps/push-helps.component';
import { PushWarningsComponent } from '../push-warnings/push-warnings.component';
import { PushWhoUsesItComponent } from '../push-who-uses-it/push-who-uses-it.component';
import { RelationalRecommendationComponent } from '../relational-recommendation/relational-recommendation.component';
import { SectionBillComponent } from '../section-bill/section-bill.component';
import { SectionOfferComponent } from '../section-offer/section-offer.component';
import { SharedServicesComponent } from '../shared-services/shared-services.component';
import { TagsInfosComponent } from '../tags-infos/tags-infos.component';

@Component({
    selector: 'dashboard',
    templateUrl: 'dashboard.html',
    styleUrls: ['dashboard.css'],
    standalone: true,
    imports: [
        MegapushComponent,
        ForbiddenComponent,
        NgClass,
        TagsInfosComponent,
        PushAlertsComponent,
        PushWarningsComponent,
        PushHelpsComponent,
        RelationalRecommendationComponent,
        PushWhoUsesItComponent,
        SectionBillComponent,
        SectionOfferComponent,
        PushChangeOfferComponent,
        ItemComponent,
        PushCommercialComponent,
        SharedServicesComponent,
        EcmMaterialModule,
        AsyncPipe,
        InsecablePipe,
    ],
})
export class DashboardComponent extends AbstractParentComponent implements OnInit, TrackableView, OnChanges {
    @Input()
    public queryParams?: HttpParams;

    public dashboardViewModel: DashboardV7ViewModel = null;

    public internetStatus: DeviceStatus = new DeviceStatus();
    public wifiStatus: DeviceStatus = new DeviceStatus();
    public voipStatus: DeviceStatus = new DeviceStatus();
    public tvStatus: DeviceStatus = new DeviceStatus();

    public deviceStatus: {
        internetStatus: DeviceStatus;
        wifiStatus: DeviceStatus;
        voipStatus: DeviceStatus;
        tvStatus: DeviceStatus;
    } = { internetStatus: undefined, wifiStatus: undefined, voipStatus: undefined, tvStatus: undefined };

    public zid: string;
    public MessageMixed: string;
    public trackingName: string;
    public deviceStatusTranslation: any;
    public actions: Array<DeviceAction>;
    public TESTER_DEPANNER_BASE_URL: string;

    public paramsHomeLan = signal([]);
    public currentCid = signal('');

    private static CLASS_NAME = 'DashboardComponent';
    private readonly pageId: string;
    private isActionsKo = false;

    private readonly supportQueryMap: Map<string, string> = new Map()
        .set('internet', 'service=HD_CNX&symptome=ACCES_KO')
        .set('wifi', 'service=HD_CNX&symptome=WIFI')
        .set('tv', 'service=HD_TV_ADSL_NEW_TV&symptome=ACCES_KO')
        .set('voip', 'service=HD_VOIP_SIP&symptome=EM_REC_KO');

    private readonly _breakpointService: BreakpointService = inject(BreakpointService);
    public isMobileWidth = toSignal(this._breakpointService.isMobileWidth());

    public constructor(
        protected uaService: UaService,
        protected routerService: RouterService,
        protected route: ActivatedRoute,
        private readonly linkService: LinkService,
        private readonly trackingService: TrackingService,
        private readonly dashboardService: DashboardService,
        private readonly errorPageService: ErrorPageService,
        private readonly portfolioService: PortfolioService,
        private readonly translate: TranslateService,
        private homelanService: HomeLanService,
        private readonly environmentService: EnvironmentService,
    ) {
        super(uaService, routerService, route);
        translate.use('fr');
        this.pageId = IdUtils.generateRandomId();
        this.TESTER_DEPANNER_BASE_URL = this.environmentService.instant('API.DASHBOARD.TESTER_DEPANNER_BASE_URL');
    }

    public ngOnInit() {
        super.ngOnInit();
        this.isSoshBrand().subscribe((isSosh) => {
            if (isSosh) {
                this.zid = this.isMobile
                    ? TmsCodes.care_digital_relational_sosh_zidMob
                    : TmsCodes.care_digital_relational_sosh_zid;
            } else {
                this.zid = this.isMobile
                    ? TmsCodes.care_digital_relational_zidMob
                    : TmsCodes.care_digital_relational_zid;
            }
        });
    }

    public eff = effect(() => {
        if (this.paramsHomeLan().length > 0) {
            EcmDispatcherModule.sendConsoleInfo(
                DashboardComponent.CLASS_NAME,
                'eff : Signal effect, will calculate status from Homelan ',
            );
            this.getStatusFromHomlan();
        }
    });

    public getDashboard(): Observable<DashboardV7> {
        return this.dashboardService
            .getDashboard(KenobiCodes.ACCMS, this.pageId, this.queryParams)
            .pipe(take(1))
            .pipe(
                map((dashboard: DashboardV7) => {
                    return dashboard;
                }),
            );
    }

    /**
     *  the OnInit interface has a hook method named ngOnInit() that Angular calls shortly after creating the component
     *  Subscribes on getMock method
     *  Uses EcmDispatcherModule to send the page title
     *  Sends data to EcmComscoreService such as the feature name, either the current contract is sosh or not, ...
     */
    public ngOnChanges(changes: SimpleChanges): void {
        // this code is wrapped in queryPrams Observable because this is the only way to re-execute
        // it while staying on the same route => we have to change brand without changing  the current route,
        // so only queryPrams state will change and re-trigger ngOnInit
        this.deviceStatusTranslation = this.translate.instant('deviceStatus');

        if (changes.queryParams && changes.queryParams.currentValue) {
            if (this.dashboardViewModel) {
                this.dashboardViewModel = null;
            }
            EcmDispatcherModule.sendSurveyParams({ section: 'NEC', idPage: 'gt-home-page' });
            EcmDispatcherModule.sendBackRoute(null);

            const dashboardTranslation = this.translate.instant('dashboard');

            this.getDashboard().subscribe((dashboard: DashboardV7) => {
                if (dashboard) {
                    this.currentCid.set(dashboard.contracts?.[0]?.cid);

                    this.dashboardViewModel = DashboardV7ViewModelMapper.getDashboardViewModel(
                        dashboard,
                        dashboardTranslation,
                    );

                    this.onAfterViewLoaded();
                    this.actions = [];

                    this.dashboardViewModel.sections?.forEach((section) => {
                        // Here we construct the relation Object
                        this.setRelation(section);
                        const hasLivebox = section.itemsHome?.items.find((item) => item.name === 'Livebox');

                        if (hasLivebox?.actionsKo) {
                            this.isActionsKo = true;
                        }

                        section.itemsHome?.items.forEach(
                            (item) => (this.actions = [].concat(this.actions, item.actions)),
                        );
                        section.itemsEntertainment.forEach(
                            (item) => (this.actions = [].concat(this.actions, item.actions)),
                        );
                    });

                    const statusFromActions = this.getStatusFromActions();
                    if (statusFromActions && statusFromActions.length > 0) {
                        EcmDispatcherModule.sendConsoleInfo(
                            DashboardComponent.CLASS_NAME,
                            'ngOnChanges : We call homelan to calculate status when [statusFromActions] is ' +
                                JSON.stringify(statusFromActions),
                        );
                        this.paramsHomeLan.set(statusFromActions);
                    }
                }
            });
        }
    }

    private setRelation(section: SectionV7ViewModel): SectionV7ViewModel {
        if (section.changeMobile) {
            const urlChangeMobile = this.linkService.getLinkSync(section.changeMobile.link);
            let urlQueryParams: Params;
            if (!urlChangeMobile.includes('source=')) {
                urlQueryParams = {
                    ...section.changeMobile.button.navigationExtras.queryParams,
                    source: 'ec_dashboard_accueil_separator_changer_mobile',
                };
            } else {
                urlQueryParams = {
                    ...section.changeMobile.button.navigationExtras.queryParams,
                };
            }
            section.changeMobile.relation = new ExtendedLink({
                label: section.changeMobile.button.label,
                link: section.changeMobile.button.link,
                navigationExtras: {
                    queryParams: urlQueryParams,
                },
                shouldKeepQueryParams: true,
                trackingData: section.changeMobile.trackingData,
            });
        }
        if (section.changeOffer) {
            const urlChangeOffer = this.linkService.getLinkSync(section.changeOffer.link);
            let urlQueryParams: Params;
            if (!urlChangeOffer.includes('source=')) {
                urlQueryParams = {
                    ...section.changeOffer.button.navigationExtras.queryParams,
                    source: 'ec_dashboard_accueil_separator_changer_offer',
                };
            } else {
                urlQueryParams = {
                    ...section.changeOffer.button.navigationExtras.queryParams,
                };
            }
            section.changeOffer.relation = new ExtendedLink({
                label: section.changeOffer.button.label,
                link: section.changeOffer.button.link,
                navigationExtras: {
                    queryParams: urlQueryParams,
                },
                shouldKeepQueryParams: true,
                trackingData: section.changeOffer.trackingData,
            });
        }
        if (section.offer) {
            section.offer.relation = new ExtendedLink({
                link: section.offer.link,
                trackingData: section.offer.trackingData,
            });
        }
        return section;
    }

    public onAfterViewLoaded(): void {
        EcmDispatcherModule.sendPageLoaded({ kenobiCode: this.dashboardViewModel.kenobiCode.functionality });
        this.trackView(this.currentCid());
    }

    public getStatusFromActions(): string[] {
        EcmDispatcherModule.sendConsoleInfo(
            DashboardComponent.CLASS_NAME,
            'getStatusFromActions : Will first calculate status from Actions ',
        );
        if (!this.actions) return;
        const isMlbKo = this.actions.find((action) => action?.id === ActionEnum.mlbKo);
        if (isMlbKo || this.isActionsKo) {
            this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, 'all');
            return;
        }

        const internetParams = this.setMlbInternetParams(
            this.actions.find((action) => action?.id === ActionEnum.mlbInternetStatus),
        );
        const tvParams = this.setMlbTVParams(this.actions.find((action) => action?.id === ActionEnum.mlbTvStatus));

        return [...internetParams, ...tvParams];
    }

    public getIcon(section: SectionV7ViewModel): Observable<string> {
        const dashboardTranslation = this.translate.instant('dashboard');
        return this.isSoshBrand().pipe(
            mergeMap((isSosh: boolean) => {
                let icon: string = null;
                if (
                    !isSosh &&
                    section.brand &&
                    section.brand.toUpperCase() === BrandEnum.Sosh &&
                    this.dashboardViewModel.universe === UniverseEnum.MIXED
                ) {
                    icon = dashboardTranslation['universContract']['mixedSosh']['picto'];
                    this.MessageMixed = dashboardTranslation['universContract']['mixedSosh']['message'];
                } else if (
                    isSosh &&
                    section.brand &&
                    section.brand.toUpperCase() === BrandEnum.Orange &&
                    this.dashboardViewModel.universe === UniverseEnum.MIXED
                ) {
                    icon = dashboardTranslation['universContract']['mixedOrange']['picto'];
                    this.MessageMixed = dashboardTranslation['universContract']['mixedOrange']['message'];
                }

                return of(icon);
            }),
        );
    }

    public getStatusFromHomlan(): void {
        EcmDispatcherModule.sendConsoleInfo(
            DashboardComponent.CLASS_NAME,
            'getStatusFromHomlan : Will get status from Homelan ',
        );
        this.homelanService.fetch(this.dashboardViewModel.sections[0].contractId, this.paramsHomeLan()).subscribe({
            next: (service: any) => {
                this.updateDeviceStatus(service);
            },
            error: (err) => {
                this.handleFetchError(err);
            },
        });
    }

    public addAlertPushForStatus(key: string): void {
        switch (key) {
            case 'internet':
                this.getLink(this.dashboardViewModel.sections[0], 'internet').subscribe((link) => {
                    this.dashboardViewModel.alerts.push({
                        type: TypeEnum.Alert,
                        code: 'INTERNET_PANNE',
                        title: 'Panne internet détectée',
                        content: ['Dépanner'],
                        link,
                    });
                });
                break;
            case 'wifi':
                this.getLink(this.dashboardViewModel.sections[0], 'wifi').subscribe((link) => {
                    this.dashboardViewModel.alerts.push({
                        type: TypeEnum.Alert,
                        code: 'WIFI_PANNE',
                        title: 'Panne Wifi détectée',
                        content: ['Dépanner'],
                        link,
                    });
                });
                break;
            case 'voip':
                this.getLink(this.dashboardViewModel.sections[0], 'voip').subscribe((link) => {
                    this.dashboardViewModel.alerts.push({
                        type: TypeEnum.Alert,
                        code: 'FIXE_PANNE',
                        title: 'Panne fixe détectée',
                        content: ['Dépanner'],
                        link,
                    });
                });
                break;
            default: //TV
                this.getLink(this.dashboardViewModel.sections[0], 'tv').subscribe((link) => {
                    this.dashboardViewModel.alerts.push({
                        type: TypeEnum.Alert,
                        code: 'TV_PANNE',
                        title: 'Service TV en panne',
                        content: ['Dépanner'],
                        link,
                    });
                });
        }
    }

    public setHomelanStatus(status: string, keys: string | string[]): void {
        if (status) {
            if (typeof keys === 'string') {
                keys = [keys];
            }
            for (const key of keys) {
                switch (key) {
                    case 'wifi': {
                        this.wifiStatus = this.deviceStatusTranslation.mlbWifiStatus.find(
                            (element: any) => element.id == status,
                        );
                        // Global Object
                        this.deviceStatus.wifiStatus = this.wifiStatus;
                        break;
                    }
                    case 'voip': {
                        this.voipStatus = this.deviceStatusTranslation.mlbVoipStatus.find(
                            (element: any) => element.id == status,
                        );
                        // Global Object
                        this.deviceStatus.voipStatus = this.voipStatus;
                        break;
                    }
                    case 'internet': {
                        this.internetStatus = this.deviceStatusTranslation.mlbInternetStatus.find(
                            (element: any) => element.id == status,
                        );
                        // Global Object
                        this.deviceStatus.internetStatus = this.internetStatus;
                        break;
                    }
                    case 'tv': {
                        this.tvStatus = this.deviceStatusTranslation.mlbTvStatus.find(
                            (element: any) => element.id == status,
                        );
                        // Global Object
                        this.deviceStatus.tvStatus = this.tvStatus;
                        break;
                    }
                    default: {
                        const unKnownStatus = this.deviceStatusTranslation.all.find(
                            (element: any) => element.id == status,
                        );
                        this.voipStatus = unKnownStatus;
                        this.internetStatus = unKnownStatus;
                        this.wifiStatus = unKnownStatus;
                        this.tvStatus = unKnownStatus;
                        // Global Object
                        this.deviceStatus.voipStatus = unKnownStatus;
                        this.deviceStatus.internetStatus = unKnownStatus;
                        this.deviceStatus.wifiStatus = unKnownStatus;
                        this.deviceStatus.tvStatus = unKnownStatus;
                    }
                }
            }
        }
    }

    public doAction(event: DeviceStatus) {
        if (event.id === 'ECO_WEAK') {
            this.homelanService
                .update(this.dashboardViewModel.sections[0].contractId, { enable: false, level: 'WEAK' })
                .subscribe({
                    next: () => {
                        this.trackingService.trackKenobi(
                            new KenobiParams(
                                this.dashboardViewModel.kenobiCode.functionality,
                                this.dashboardViewModel.kenobiCode.rubric,
                                Status.OK,
                            ),
                            this.pageId,
                            false,
                        );
                        EcmDispatcherModule.sendConsoleInfo(
                            DashboardComponent.CLASS_NAME,
                            'doAction : Will update homelan status from click ecomode button',
                        );
                        this.paramsHomeLan.set(['internet', 'wifi', 'voip', 'tv']);
                    },
                    error: (error: HttpErrorResponse) => {
                        this.trackingService.trackKenobi(
                            new KenobiParams(
                                this.dashboardViewModel.kenobiCode.functionality,
                                this.dashboardViewModel.kenobiCode.rubric,
                                error.status > 500 ? Status.KOSI : Status.KORG,
                            ),
                            this.pageId,
                            false,
                        );
                        this.errorPageService.showDefaultErrorPage();
                    },
                });
        }
    }

    private updateDeviceStatus(service: any): void {
        EcmDispatcherModule.sendConsoleInfo(
            DashboardComponent.CLASS_NAME,
            'updateDeviceStatus : Will calculate status from homelan fetch call, service = ' + JSON.stringify(service),
        );
        if (!this.dashboardViewModel.alerts) {
            this.dashboardViewModel.alerts = [];
        }

        this.updateVoipStatus(service);
        this.updateEcoModeStatus(service);

        EcmDispatcherModule.sendConsoleInfo(
            DashboardComponent.CLASS_NAME,
            'Will have DeviceStatus objects > internet :  ' +
                JSON.stringify(this.internetStatus) +
                ' voip : ' +
                JSON.stringify(this.voipStatus) +
                ' wifi : ' +
                JSON.stringify(this.wifiStatus) +
                ' tv : ' +
                JSON.stringify(this.tvStatus) +
                ' all in deviceStatus : ' +
                JSON.stringify(this.deviceStatus),
        );
    }

    private updateVoipStatus(service: any): void {
        if (this.paramsHomeLan().length === 1 && this.paramsHomeLan()[0] === 'voip') {
            this.setHomelanStatus(service.voip, 'voip');
            if (service.voip === HomeLanServiceStatusEnum.UNAVAILABLE) {
                this.addAlertPushForStatus('voip');
            }
        }
    }

    private updateEcoModeStatus(service: any): void {
        if (service?.ecoMode === HomeLanServiceStatusEnum.WEAK) {
            this.updateWeakStatus(service);
        } else if (service?.ecoMode === HomeLanServiceStatusEnum.DEEP) {
            this.updateDeepStatus(service);
        } else if (this.paramsHomeLan().includes('internet')) {
            this.updateInternetStatus(service);
        }
    }

    private updateWeakStatus(service: any): void {
        this.setHomelanStatus(HomeLanServiceStatusEnum.ECO_WEAK, ['internet', 'wifi']);
        if (service?.tv === HomeLanServiceStatusEnum.DISABLED) {
            this.setHomelanStatus(HomeLanServiceStatusEnum.ECO_WEAK, 'tv');
        }
        if (service?.voip) {
            this.setHomelanStatus(service.voip, 'voip');
        }
    }

    private updateDeepStatus(service: any): void {
        this.setHomelanStatus(HomeLanServiceStatusEnum.ECO_DEEP, ['internet', 'wifi']);
        if (service?.tv === HomeLanServiceStatusEnum.DISABLED) {
            this.setHomelanStatus(HomeLanServiceStatusEnum.ECO_DEEP, 'tv');
        }
        this.setHomelanStatus(HomeLanServiceStatusEnum.DISABLED, 'voip');
    }

    private updateInternetStatus(service: any): void {
        this.setHomelanStatus(service.internet, 'internet');
        if (service.internet === HomeLanServiceStatusEnum.UNAVAILABLE) {
            this.addAlertPushForStatus('internet');
            this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, ['wifi', 'voip', 'tv']);
        } else {
            this.setHomelanStatus(service.voip, 'voip');
            this.setHomelanStatus(service.wifi, 'wifi');
            if (service.wifi === HomeLanServiceStatusEnum.UNAVAILABLE) {
                this.addAlertPushForStatus('wifi');
            }
            if (service.voip === HomeLanServiceStatusEnum.UNAVAILABLE) {
                this.addAlertPushForStatus('voip');
            }
            if (this.paramsHomeLan().includes('tv')) {
                this.setHomelanStatus(service.tv, 'tv');
                if (service.tv === HomeLanServiceStatusEnum.UNAVAILABLE) {
                    this.addAlertPushForStatus('tv');
                }
            }
        }
    }

    private trackView(currentCid?: string): void {
        if (currentCid == null) {
            return;
        }

        this.portfolioService.getContractByCid(currentCid).subscribe((loggedContract) => {
            const hasCybersecureOption = loggedContract?.options?.some((option: OptionCode) =>
                [OptionCode.PACK_CYBER_HOME, OptionCode.PACK_CYBER_MOBILE].includes(option),
            );

            const trackData = {
                trackingLabel: this.dashboardViewModel.trackingLabel,
                contrat: {
                    offerType: loggedContract?.offerType,
                    offerLabel: loggedContract?.offerName,
                    brand: loggedContract?.brand,
                    role: loggedContract?.role,
                    hasCybersecureOption,
                },
            };

            this.trackingService.trackDashboardView(trackData);
            this.trackingService.trackKenobi(
                new KenobiParams(
                    this.dashboardViewModel.kenobiCode.functionality,
                    this.dashboardViewModel.kenobiCode.rubric,
                    Status.OK,
                ),
                this.pageId,
                false,
                currentCid,
            );
        });

        if (this.dashboardViewModel.sections.length > 0) {
            this.dashboardViewModel.sections.forEach((element) => {
                this.zid = this.trackingService.getTrackNamePerType(element, this.isMobile, this.isSoshBrand());
            });
        }
    }

    private handleFetchError(err: any): void {
        switch (err.status) {
            case 404: {
                if (this.paramsHomeLan().includes('internet')) {
                    this.setHomelanStatus(HomeLanServiceStatusEnum.UNAVAILABLE, ['internet']);
                    this.addAlertPushForStatus('internet');
                }
                if (this.paramsHomeLan().includes('voip')) {
                    this.setHomelanStatus(HomeLanServiceStatusEnum.UNAVAILABLE, ['voip']);
                }
                if (this.paramsHomeLan().includes('tv')) {
                    this.setHomelanStatus(HomeLanServiceStatusEnum.UNAVAILABLE, ['tv']);
                }
                break;
            }
            case 415: {
                this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, ['all']);
                break;
            }
            case 460: {
                this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, ['all']);
                this.dashboardViewModel.infos.push({
                    type: TypeEnum.Info,
                    code: 'KO_CONVERGENCE',
                    title: "Une erreur technique nous empêche momentanément d'afficher certaines informations",
                });
                break;
            }
            case 421: {
                this.setHomelanStatus(HomeLanServiceStatusEnum.DISCONNECTED, ['internet']);
                this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, ['wifi', 'voip', 'tv']);
                break;
            }
            default: {
                this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, ['all']);
            }
        }
    }

    private setMlbInternetParams(deviceAction: DeviceAction): string[] {
        const params = [];
        if (!deviceAction) {
            this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, ['internet', 'wifi', 'voip']);
        } else if (
            deviceAction.status === HomeLanServiceStatusEnum.ECO_WEAK ||
            deviceAction.status === HomeLanServiceStatusEnum.STANDBY_WEAK
        ) {
            this.setHomelanStatus(deviceAction.status, ['internet', 'wifi']);
            params.push('voip');
        } else if (
            deviceAction.status === HomeLanServiceStatusEnum.STANDBY_DEEP ||
            deviceAction.status === HomeLanServiceStatusEnum.ECO_DEEP
        ) {
            this.setHomelanStatus(deviceAction.status, ['internet', 'wifi', 'voip']);
        } else {
            params.push('internet', 'wifi', 'voip');
        }
        return params;
    }

    private setMlbTVParams(deviceAction: DeviceAction): string[] {
        const params = [];
        if (!deviceAction) {
            this.setHomelanStatus(HomeLanServiceStatusEnum.UNKNOWN, 'tv');
        } else if (
            deviceAction.status === HomeLanServiceStatusEnum.STANDBY_WEAK ||
            deviceAction.status === HomeLanServiceStatusEnum.STANDBY_DEEP ||
            deviceAction.status === HomeLanServiceStatusEnum.ECO_WEAK ||
            deviceAction.status === HomeLanServiceStatusEnum.ECO_DEEP
        ) {
            this.setHomelanStatus(deviceAction.status, 'tv');
        } else {
            params.push('tv');
        }
        return params;
    }
    public getTesterDepannerUrl(contract: any): Observable<string> {
        const cid = contract.contractId;
        const brand = contract.brand.toLowerCase();
        const domain = this.getDomain(brand);

        return this.homelanService.getExternalId(cid).pipe(
            map((externalId) => {
                return this.TESTER_DEPANNER_BASE_URL.replace('{cfsid}', externalId)
                    .replace('{cid}', cid)
                    .replace('{brand}', brand)
                    .replace('{domain}', domain);
            }),
        );
    }
    public getDomain(brand: string): string {
        if (brand === BrandEnum.Orange.toLowerCase()) {
            return TesterDepannerDomainEnum.GP;
        }
        return TesterDepannerDomainEnum.SOSH;
    }

    public getLink(contract: any, service: string): Observable<Link> {
        const supportQuery = this.supportQueryMap.get(service);

        return this.getTesterDepannerUrl(contract).pipe(
            map((baseUrl: string) => {
                const link = new Link();
                link.browser = `${baseUrl}&${supportQuery}`;
                return link;
            }),
        );
    }
}
